import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';

import { SUCCESS_GREEN_COLOR } from '@/constants/color';
import { Address as AddressInterface } from '@/types/person';
import Alert from '@/ui/components/Alert';

import { Props } from './types';

const Address = ({ address, showAddressFieldsOnly = false, showCountry = false }: Props) => {
  const {
    address1,
    address2,
    city,
    mailing_instructions,
    state,
    postal_code,
    delivery_point_barcode,
    dpv_match_code
  } = address;

  const dpvMatchCodeMessages: Record<string, string> = {
    N: 'Not confirmed; the address is not present in the USPS data.',
    S: 'Confirmed by ignoring secondary info; the main address is present in the USPS data, but the submitted secondary information (apartment, suite, etc.) was not recognized.',
    D: 'Confirmed but missing secondary info; the main address is present in the USPS data, but it is missing secondary information (apartment, suite, etc.).'
  };

  const dpvMatchCodeMessage = dpv_match_code ? dpvMatchCodeMessages[dpv_match_code] : undefined;

  const addressType = useMemo(() => {
    if (Object.hasOwn(address, 'address_type')) {
      const { address_type } = address as AddressInterface;
      return address_type?.name;
    }

    return null;
  }, [address]);

  const addressName = useMemo(() => {
    if (address.first_name || address.last_name) {
      return `${address.first_name || ''} ${address.last_name || ''}`.trim();
    }

    return null;
  }, [address.first_name, address.last_name]);

  return (
    <address className="not-italic">
      {!showAddressFieldsOnly && delivery_point_barcode && (
        <p>
          <FontAwesomeIcon icon={faCheckCircle} className={`${SUCCESS_GREEN_COLOR} text-lg pt-1`} />{' '}
          Validated
        </p>
      )}
      {!showAddressFieldsOnly && addressType && <p>{addressType}</p>}
      <div className="mp-sensitive">
        {!showAddressFieldsOnly && addressName && <p>{addressName}</p>}
        {address1 && <p>{address1}</p>}
        {address2 && <p>{address2}</p>}
        {(city || state || postal_code) && (
          <p>
            {city && `${city}, `}
            {state && state}
            {postal_code && ` ${postal_code}`}
          </p>
        )}
        {showCountry && address.country && <p>{address.country}</p>}
      </div>
      {!showAddressFieldsOnly && mailing_instructions && (
        <p>
          Mailing Instructions: <span className="mp-sensitive">{mailing_instructions}</span>
        </p>
      )}
      {dpvMatchCodeMessage && (
        <div className="mb-2">
          <Alert level="warn" message={dpvMatchCodeMessage} />
        </div>
      )}
    </address>
  );
};

export default Address;
